/**
 * 3D Spatial Grid Hash for fast neighbor queries.
 * Optimized for axis-aligned cuboids with O(1) cell lookups.
 * Used by the Ambient Occlusion system to find nearby cuboids.
 */
export default class AOSpatialIndex {
  /**
   * Create a new spatial index.
   * @param {number} cellSize - Size of each grid cell in world units (default 2)
   */
  constructor(cellSize = 2) {
    this.cellSize = cellSize;
    // Map from cell key "cx,cy,cz" to Set of cuboids in that cell
    this.grid = new Map();
    // Map from cuboid key to Set of cell keys it occupies
    this.cuboidCells = new Map();
  }

  /**
   * Get the cell key for a world position.
   * @param {number} x - World X coordinate
   * @param {number} y - World Y coordinate
   * @param {number} z - World Z coordinate
   * @returns {string} Cell key "cx,cy,cz"
   */
  getCellKey(x, y, z) {
    const cx = Math.floor(x / this.cellSize);
    const cy = Math.floor(y / this.cellSize);
    const cz = Math.floor(z / this.cellSize);
    return `${cx},${cy},${cz}`;
  }

  /**
   * Insert a cuboid into the spatial index.
   * Adds it to all cells it overlaps.
   * @param {Cuboid} cuboid - The cuboid to insert
   */
  insert(cuboid) {
    const key = cuboid.getKey();

    // Skip if already inserted
    if (this.cuboidCells.has(key)) {
      return;
    }

    const cells = new Set();

    // Find all cells this cuboid overlaps
    const minCx = Math.floor(cuboid.x / this.cellSize);
    const maxCx = Math.floor((cuboid.x + cuboid.width) / this.cellSize);
    const minCy = Math.floor(cuboid.y / this.cellSize);
    const maxCy = Math.floor((cuboid.y + (cuboid.collisionHeight || cuboid.height)) / this.cellSize);
    const minCz = Math.floor(cuboid.z / this.cellSize);
    const maxCz = Math.floor((cuboid.z + cuboid.depth) / this.cellSize);

    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        for (let cz = minCz; cz <= maxCz; cz++) {
          const cellKey = `${cx},${cy},${cz}`;
          cells.add(cellKey);

          // Add cuboid to this cell
          if (!this.grid.has(cellKey)) {
            this.grid.set(cellKey, new Set());
          }
          this.grid.get(cellKey).add(cuboid);
        }
      }
    }

    // Track which cells this cuboid is in
    this.cuboidCells.set(key, cells);
  }

  /**
   * Remove a cuboid from the spatial index.
   * @param {string} cuboidKey - The cuboid's key to remove
   */
  remove(cuboidKey) {
    const cells = this.cuboidCells.get(cuboidKey);
    if (!cells) return;

    // Remove from each cell
    for (const cellKey of cells) {
      const cell = this.grid.get(cellKey);
      if (cell) {
        // Find and remove the cuboid with this key
        for (const cuboid of cell) {
          if (cuboid.getKey() === cuboidKey) {
            cell.delete(cuboid);
            break;
          }
        }
        // Clean up empty cells
        if (cell.size === 0) {
          this.grid.delete(cellKey);
        }
      }
    }

    this.cuboidCells.delete(cuboidKey);
  }

  /**
   * Query all cuboids within a radius of a point.
   * Returns unique cuboids (no duplicates from overlapping cells).
   * @param {number} x - Center X coordinate
   * @param {number} y - Center Y coordinate
   * @param {number} z - Center Z coordinate
   * @param {number} radius - Search radius in world units
   * @returns {Set<Cuboid>} Set of cuboids within radius
   */
  queryRadius(x, y, z, radius) {
    const results = new Set();

    // Calculate cell range to search
    const minCx = Math.floor((x - radius) / this.cellSize);
    const maxCx = Math.floor((x + radius) / this.cellSize);
    const minCy = Math.floor((y - radius) / this.cellSize);
    const maxCy = Math.floor((y + radius) / this.cellSize);
    const minCz = Math.floor((z - radius) / this.cellSize);
    const maxCz = Math.floor((z + radius) / this.cellSize);

    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        for (let cz = minCz; cz <= maxCz; cz++) {
          const cellKey = `${cx},${cy},${cz}`;
          const cell = this.grid.get(cellKey);
          if (cell) {
            for (const cuboid of cell) {
              results.add(cuboid);
            }
          }
        }
      }
    }

    return results;
  }

  /**
   * Query all cuboids overlapping an axis-aligned bounding box.
   * @param {number} minX - Minimum X
   * @param {number} minY - Minimum Y
   * @param {number} minZ - Minimum Z
   * @param {number} maxX - Maximum X
   * @param {number} maxY - Maximum Y
   * @param {number} maxZ - Maximum Z
   * @returns {Set<Cuboid>} Set of cuboids overlapping the AABB
   */
  queryAABB(minX, minY, minZ, maxX, maxY, maxZ) {
    const results = new Set();

    const minCx = Math.floor(minX / this.cellSize);
    const maxCx = Math.floor(maxX / this.cellSize);
    const minCy = Math.floor(minY / this.cellSize);
    const maxCy = Math.floor(maxY / this.cellSize);
    const minCz = Math.floor(minZ / this.cellSize);
    const maxCz = Math.floor(maxZ / this.cellSize);

    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        for (let cz = minCz; cz <= maxCz; cz++) {
          const cellKey = `${cx},${cy},${cz}`;
          const cell = this.grid.get(cellKey);
          if (cell) {
            for (const cuboid of cell) {
              results.add(cuboid);
            }
          }
        }
      }
    }

    return results;
  }

  /**
   * Rebuild the entire spatial index from a cuboid map.
   * @param {Map<string, Cuboid>} cuboids - Map of cuboid key to Cuboid
   */
  rebuild(cuboids) {
    this.clear();
    for (const cuboid of cuboids.values()) {
      this.insert(cuboid);
    }
  }

  /**
   * Clear all data from the spatial index.
   */
  clear() {
    this.grid.clear();
    this.cuboidCells.clear();
  }

  /**
   * Get statistics about the spatial index.
   * @returns {Object} Statistics object
   */
  getStats() {
    let totalCuboids = 0;
    let maxPerCell = 0;

    for (const cell of this.grid.values()) {
      totalCuboids += cell.size;
      maxPerCell = Math.max(maxPerCell, cell.size);
    }

    return {
      cellCount: this.grid.size,
      cuboidCount: this.cuboidCells.size,
      avgPerCell: this.grid.size > 0 ? (totalCuboids / this.grid.size).toFixed(2) : 0,
      maxPerCell
    };
  }
}
